import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const projects = [
  {
    
    title: "AI-Integrated Product Development",
    headline: "User-facing AI that actually works",
    excerpt: "We build user‑facing and internal applications that earn their keep and behave like reliable software, not impressive demos. We embed AI where it improves the product and the business. All with engineering discipline.",
    image: "/images/project1.jpg"
  },
  {
    title: "Traditional AI & Machine Learning",
    headline: "Classical ML when it's the better answer",
    excerpt: "Many problems are still best solved with proven methods. We deliver forecasting, risk scoring, recommendations, and optimization using classical ML and statistics, with the same production rigor as our LLM work.",
    image: "/images/project2.jpg"
  },
  {
    title: "Platform Engineering",
    headline: "Make the right way the easy way",
    excerpt: "We build internal platforms that accelerate your teams: paved-road templates, golden paths for CI/CD, and shared services for identity, secrets, and AI evaluation. Measured improvements in cycle time and deployment frequency.",
    image: "/images/project3.jpg"
  }
  // ,
  // {
  //   title: "Security & Trust",
  //   headline: "Security as a system property, not a gate",
  //   excerpt: "We harden software supply chains with SBOMs, provenance tracking, and SLSA-aligned builds.",
  //   image: "/images/project3.jpg"
  // }
];

const ServicesSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative overflow-hidden" id="services">
      <div className="absolute left-0 top-0 w-full h-2/3 lg:px-4">
        <div className="rounded-2xl top-0 w-full h-full bg-custom-indigo-500"></div>
      </div>
      <div className="container relative py-16 lg:py-24 mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap -mx-3 mb-12 text-white justify-center items-end"
        >
          <div className="w-full lg:w-2/3 px-3 text-white text-center">
            <h6 className="mb-5 text-white/75 text-xl font-medium">What we do</h6>
            <h2 className="text-4xl lg:text-5xl font-medium">We build for the market you have today and the one you intend to win</h2>
          </div>
        </motion.div>
        
        <div className="flex flex-wrap -mx-3">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="w-full lg:w-1/3 px-3 mb-8"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg group hover:shadow-2xl transition-shadow duration-300">
                <div className="overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                 <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-custom-indigo-500 text-sm font-medium">{project.headline}</span>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {project.excerpt}
                  </p>
                  {/* <a href="#" className="text-custom-indigo-500 font-medium hover:underline">
                    Read More →
                  </a> */}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;